<script setup lang="ts">

import LingoGiveawayHeader from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayHeader.vue'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import HatchEggInfoModal from '@/partners/lingo/lingo-giveaway/components/modals/HatchEggInfoModal.vue'
import HatchOptionsSection from '@/partners/lingo/lingo-giveaway/components/HatchOptionsSection.vue'
import LingoGiveawayDrawSection from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayDrawSection.vue'

import { syncLingoGiveaway, useLingoGiveaway } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'
import DropeeSpinnerIcon from '@/components/icons/DropeeSpinnerIcon.vue'
import LingoGiveawayEggGame from '@/partners/lingo/lingo-giveaway/components/LingoGiveawayEggGame.vue'
import { useNow } from '@/composables/game/time'
import { useLingoGameSounds } from '@/partners/lingo/lingo-giveaway/composables/sounds'

const { countDownSeconds, resetTodayEntriesCount } = useLingoGiveaway()
const { nowDateIso } = useNow()
const showHatchEggInfoModal = ref<boolean>(false)
const isLoading = ref<boolean>(true)

const { playIslandSound, stopAllSounds, islandSoundStarted, muteSounds, unmuteSounds } = useLingoGameSounds()
const checkGlowSound = ref(0)

watch(nowDateIso, () => {
  resetTodayEntriesCount()
  syncLingoGiveaway()
})

function onPageClick() {
  if (islandSoundStarted.value) return
  playIslandSound()
  checkGlowSound.value++
}

onMounted(async () => {
  await syncLingoGiveaway()
  await new Promise(r => setTimeout(r, 1000))
  isLoading.value = false
})

onBeforeUnmount(() => {
  stopAllSounds()
})

watch(isLoading, isLoading => {
  if (isLoading) {
    muteSounds()
  } else {
    unmuteSounds()
  }
}, { immediate: true })
</script>
<template>
  <div
    class="py-6 px-4 flex flex-col gap-y-3 flex-1"
    @touchstart="onPageClick"
    @click.capture="onPageClick"
  >
    <Transition
      name="fade"
      mode="out-in"
    >
      <div
        class="w-full flex-1 flex items-center justify-center fixed inset-0 z-[1]"
        v-if="isLoading"
      >
        <DropeeSpinnerIcon class="w-12 h-12 min-w-[48px] text-[#7A1BCD]" />
      </div>
    </Transition>
    <LingoGiveawayHeader />
    <div
      class="flex flex-col w-full gap-y-3 transition-opacity duration-300"
      :class="isLoading ? 'opacity-0' : 'opacity-100'"
    >
      <div class="flex flex-col w-full gap-y-6">
        <LingoGiveawayEggGame
          class="mb-8"
          :check-glow-sound="checkGlowSound"
        />
        <HatchOptionsSection :remained-time="countDownSeconds" />
      </div>
      <LingoGiveawayDrawSection />
      <HatchEggInfoModal
        :show="showHatchEggInfoModal"
        @hide="showHatchEggInfoModal = false"
      />
    </div>
  </div>
</template>

<style lang="scss">
@import '@/partners/lingo/lingo-giveaway/assets/styles/global'
</style>
